package com.arena_web.ui.sections.profile.mobile

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.FollowUserDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.edit.GradientBorderRing
import com.arena_web.ui.sections.profile.edit.InterestOption
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.bodyXlMedium
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

// `Interactive/Secondary` fill + `Button/border/gradient/primary` hairline over
// an opaque body — the outline-button recipe shared by the mobile sheets.
private val FieldBackground =
    "${AppGradients.InteractiveSecondary} padding-box, " +
        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
        "${AppGradients.ButtonBorderGradientPrimary} border-box"

/**
 * Mobile unfollow confirmation — the phone counterpart to the desktop
 * [com.arena_web.ui.components.overlay.FollowConfirmDialog], re-laid-out as a
 * [BottomSheet]. Shows the user's card/avatar, the prompt, and Cancel / confirm.
 */
@Composable
fun MobileUnfollowConfirmSheet(
    user: FollowUserDto,
    title: String,
    subtitle: String? = null,
    confirmLabel: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
) {
    val t = LocalAppStrings.current
    BottomSheet(onDismiss = onCancel) {
        Column(
            modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(22)) },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(8)) },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                ConfirmCard(user)
                SpanText(
                    title,
                    modifier = Modifier.bodyXlMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
                if (subtitle != null) {
                    SpanText(
                        subtitle,
                        modifier = Modifier
                            .fillMaxWidth()
                            .bodyMRegular()
                            .color(Color("#99b1bf"))
                            .textAlign(TextAlign.Center)
                            .styleModifier { property("line-height", s(22)) }
                    )
                }
            }

            Row(
                modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(12)) }
            ) {
                Box(
                    modifier = Modifier
                        .styleModifier {
                            property("flex", "1")
                            property("height", s(46))
                            property("border-radius", s(100))
                            property("border", "0.3px solid transparent")
                            property("background", FieldBackground)
                            property("box-sizing", "border-box")
                        }
                        .cursor(Cursor.Pointer)
                        .onClick { onCancel() },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(t.commonCancel, modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2))
                }
                Box(
                    modifier = Modifier
                        .styleModifier {
                            property("flex", "1")
                            property("height", s(46))
                            property("border-radius", s(100))
                            property("background", "#0061ff")
                        }
                        .cursor(Cursor.Pointer)
                        .onClick { onConfirm() },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(confirmLabel, modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite))
                }
            }
        }
    }
}

@Composable
private fun ConfirmCard(user: FollowUserDto) {
    val cardFrameUrl = user.cardImageUrl
    Box(
        modifier = Modifier.styleModifier {
            property("flex-shrink", "0")
            property("width", s(48))
            property("height", s(72))
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
        },
        contentAlignment = Alignment.Center
    ) {
        if (cardFrameUrl != null) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = "football",
                size = PlayerCardSize.Highlight,
                cardFrameUrl = cardFrameUrl,
                avatarUrl = user.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                hideRating = true,
                hideShadow = true,
            )
        } else {
            val avatarUrl = user.avatarUrl
            Box(
                modifier = Modifier
                    .sWidth(48)
                    .sHeight(72)
                    .styleModifier {
                        property("border-radius", s(8))
                        property("background", "rgba(60, 121, 241, 0.15)")
                        property("overflow", "hidden")
                    },
                contentAlignment = Alignment.Center
            ) {
                if (avatarUrl != null) {
                    Image(src = avatarUrl, modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover))
                } else {
                    SpanText(
                        user.username.take(1).uppercase(),
                        modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
            }
        }
    }
}

/**
 * Mobile read-only preference-details — the phone counterpart to the desktop
 * [com.arena_web.ui.components.overlay.DetailsPreferenceDialog], re-laid-out as
 * a [BottomSheet]. Surfaces the tapped interest chip + its saved note.
 */
@Composable
fun MobileInterestNoteSheet(
    option: InterestOption?,
    note: String,
    onDismiss: () -> Unit,
) {
    val t = LocalAppStrings.current
    BottomSheet(onDismiss = onDismiss) {
        Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(22)) }) {
            Row(
                modifier = Modifier.fillMaxWidth().sGap(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (option != null) ReadOnlyInterestChip(option)
                SpanText("—", modifier = Modifier.sFontSize(20).color(com.arena_web.ui.theme.ThemeVars.Text2))
                SpanText(
                    t.profileDialogPreferenceDetails,
                    modifier = Modifier
                        .sFontSize(20)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(26)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sMinHeight(60)
                    .sBorderRadius(12)
                    .styleModifier {
                        property("position", "relative")
                        property(
                            "background",
                            "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(48, 108, 206, 0.2) 100%)"
                        )
                    }
            ) {
                GradientBorderRing(radiusPx = 12)
                Box(
                    modifier = Modifier.fillMaxSize().styleModifier {
                        property("padding", "${s(8)} ${s(12)}")
                        property("display", "flex")
                        property("align-items", "center")
                    }
                ) {
                    SpanText(
                        note,
                        modifier = Modifier
                            .sFontSize(14)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(22)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
            }
        }
    }
}

@Composable
private fun ReadOnlyInterestChip(option: InterestOption) {
    Box(
        modifier = Modifier
            .sHeight(32)
            .sBorderRadius(22)
            .styleModifier {
                property("position", "relative")
                property(
                    "background",
                    "linear-gradient(0deg, rgba(12, 66, 255, 0.7) 3%, rgba(102, 102, 102, 0) 60%)"
                )
                property("display", "inline-flex")
                property("align-items", "center")
            }
    ) {
        GradientBorderRing(radiusPx = 22)
        Row(
            modifier = Modifier.sGap(8).styleModifier {
                property("padding", "${s(7)} ${s(14)}")
                property("display", "inline-flex")
                property("align-items", "center")
            },
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (option.iconSrc != null) {
                Icon(src = option.iconSrc, modifier = Modifier.sSize(16))
            }
            SpanText(
                option.label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }
    }
}
